import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { toast } from "react-toastify";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import "./Quotation.scss";
import { extendQuoteRequest } from "api";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import testIds from "constants/testIds";

interface IExtendQuoteDialogProps {
  isOpen: boolean;
  close: () => void;
  reference: any;
  refetchQuotationLists: Function;
}

function ExtendQuoteDialog(props: IExtendQuoteDialogProps) {
  const { isOpen, close, reference, refetchQuotationLists } = props;
  const id: any = useParams()?.id?.toUpperCase();
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, reset, setValue, watch } = methods;
  const { financial, service, reference: referenceId } = reference || {};

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data } = useGetQuoteRequestDetails({
    shipmentId: id,
    options: {
      enabled: !!id,
      refetchOnMount: false,
    },
  });
  const { confirmation_date_seconds_remaining } = data?.organization || {};

  const confirmationTimeRemaining = moment.duration(confirmation_date_seconds_remaining, "seconds");
  const days = confirmationTimeRemaining.days();
  const hours = confirmationTimeRemaining.hours();
  const minutes = confirmationTimeRemaining.minutes();

  const getDayHourMin = (value: number) =>
    Array.from({ length: value }, (_, index) => ({ label: index.toString(), value: index }));

  const isExtendDisabled = !watch("days") && !watch("hours") && !watch("minutes");

  const handleExtendRequest = (data: any) => {
    const { days, hours, minutes } = data;
    if (days || hours || minutes) {
      // Fill empty fields with '0'
      if (!days) {
        setValue("days", "0");
        data.days = 0;
      }
      if (!hours) {
        setValue("hours", "0");
        data.hours = 0;
      }
      if (!minutes) {
        setValue("minutes", "0");
        data.minutes = 0;
      }

      setIsSubmitting(true);
      extendQuoteRequest(reference.reference, data)
        .then(() => {
          refetchQuotationLists();
          toast(
            intl.formatMessage({
              id: "quote.quotationExtendSuccessMsg",
              defaultMessage: "Quote extended successfully",
            }),
            { type: "success" },
          );
          reset();
          close();
        })
        .catch((error) => errorToast(error))
        .finally(() => setIsSubmitting(false));
    }
  };

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "quote.quotation.extendQuote",
        defaultMessage: "Extend Quote",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleExtendRequest)} style={{ width: 380 }}>
          <p className="font-b1 font-weight-bold">
            {`${service.freight_mode_label} ${referenceId} ( ${financial.total_quoted_amount_currency} ${financial.total_quoted_amount} )`}
          </p>
          <div className="d-flex flex-col mt-4">
            <div>
              <label>
                <FormattedMessage id="quote.days" defaultMessage="Days" />
              </label>
              <div className="d-flex align-items-center">
                <div className="w-90">
                  <Dropdown useHookForm name="days" className="schedule-dropdown" options={getDayHourMin(31)} />
                </div>
              </div>
            </div>
            <div className="ml-3">
              <label className="">
                <FormattedMessage id="quote.hours" defaultMessage="Hours" />
              </label>
              <div className="d-flex align-items-center">
                <div className="w-90">
                  <Dropdown useHookForm name="hours" className="schedule-dropdown" options={getDayHourMin(24)} />
                </div>
              </div>
            </div>
            <div className="ml-3">
              <label className="">
                <FormattedMessage id="quote.minutes" defaultMessage="Minutes" />
              </label>
              <div className="d-flex align-items-center">
                <div className="w-90">
                  <Dropdown useHookForm name="minutes" className="schedule-dropdown" options={getDayHourMin(60)} />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-secondary font-b1">
              {`Customer confirms booking in ${days > 0 ? `${days} days` : "0 days"} , ${
                hours > 0 ? `${hours} hours` : " 0 hours"
              } , 
              ${minutes > 0 ? `${minutes} minutes` : "0 minutes"}`}
            </p>
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                size="lg"
                onClick={close}
                data-testid={testIds.SHIPMENT_DASHBOARD_EXTENDED_MODAL_CLOSE_BUTTON}
              />
              <Button
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({ id: "quote.extend", defaultMessage: "Extend" })}
                data-testid={testIds.SHIPMENT_DASHBOARD_EXTENDED_MODAL_EXTEND_BUTTON}
                disabled={isExtendDisabled || isSubmitting}
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
}
export default ExtendQuoteDialog;
